// ChecklistsTab — admin CRUD for the two customizable checklists:
// Required Documents + Required Information. Mirrors DeficiencyRulesTab.
//
// Required Documents: expected docs per case type / depositor type,
//   matched against uploaded docs by doc category / classification /
//   filename keywords -> Provided / Missing.
// Required Information: info items that must be present across case
//   facts + document text, matched by case field + keywords ->
//   Present / Missing.
//
// Canonical defaults are seeded from app/rules/canonical/checklist_rules.yaml;
// admin edits persist across reloads (startup seeder is idempotent).

import { useState, useEffect, useCallback } from 'react';
import { Save, X, Loader2, FileText, Info } from 'lucide-react';
import { useToastStore } from '../../stores/toast';
import {
  getRequiredDocumentRules, createRequiredDocumentRule,
  updateRequiredDocumentRule, deleteRequiredDocumentRule,
  type RequiredDocumentRule, type RequiredDocumentRuleInput,
  getRequiredInformationRules, createRequiredInformationRule,
  updateRequiredInformationRule, deleteRequiredInformationRule,
  type RequiredInformationRule, type RequiredInformationRuleInput,
} from '../../api/admin';

const DOC_CATEGORIES = ['', 'cover_page', 'supporting', 'legal_opinion', 'transfer_agent', 'other'];
const CASE_FACT_FIELDS = ['', 'issuer_name', 'issuer_symbol', 'cusip', 'cik', 'depositor_name', 'depositor_type', 'account_ref', 'deposit_date', 'share_quantity'];

export default function ChecklistsTab() {
  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500">
        Configurable checklists used on the case Overview tab + in the deficiency
        export (the copy/paste client email). Add, edit, or remove expected
        documents and required information items. Edits persist across reloads.
      </p>
      <DocumentRulesSection />
      <InformationRulesSection />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Required Documents
// ---------------------------------------------------------------------------

const EMPTY_DOC: RequiredDocumentRuleInput = {
  label: '', description: '', case_type: null, depositor_type: null,
  match_doc_category: null, match_classification: null, match_keywords: null,
  enabled: true, priority: 0,
};

function DocumentRulesSection() {
  const addToast = useToastStore((s) => s.addToast);
  const [rules, setRules] = useState<RequiredDocumentRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<RequiredDocumentRuleInput>(EMPTY_DOC);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRules(await getRequiredDocumentRules());
    } catch { addToast('Failed to load required-document rules', 'error'); }
    finally { setLoading(false); }
  }, [addToast]);

  useEffect(() => { void load(); }, [load]);

  const reset = () => { setEditingId(null); setForm(EMPTY_DOC); };

  const startEdit = (r: RequiredDocumentRule) => {
    setEditingId(r.id);
    setForm({
      label: r.label, description: r.description,
      case_type: r.case_type, depositor_type: r.depositor_type,
      match_doc_category: r.match_doc_category, match_classification: r.match_classification,
      match_keywords: r.match_keywords, enabled: r.enabled, priority: r.priority,
    });
  };

  const handleSave = async () => {
    if (!form.label.trim()) { addToast('Label is required', 'error'); return; }
    setSaving(true);
    try {
      const payload = { ...form, label: form.label.trim() };
      if (editingId) {
        await updateRequiredDocumentRule(editingId, payload);
        addToast('Required-document rule updated', 'success');
      } else {
        await createRequiredDocumentRule(payload);
        addToast('Required-document rule created', 'success');
      }
      reset(); await load();
    } catch (e: any) { addToast(e?.response?.data?.detail || 'Failed to save', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    setSaving(true);
    try { await deleteRequiredDocumentRule(id); addToast('Rule deleted', 'success'); await load(); }
    catch (e: any) { addToast(e?.response?.data?.detail || 'Failed to delete', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <Section
      icon={<FileText size={15} />}
      title="Required Documents"
      subtitle="Expected documents for a case — matched against uploaded docs by category / classification / filename keywords."
      loading={loading}
      editingId={editingId}
      form={form as any}
      setForm={setForm as any}
      reset={reset}
      onSave={handleSave}
      saving={saving}
      rules={rules}
      onStartEdit={startEdit}
      onDelete={handleDelete}
      fields={[
        { key: 'label', label: 'Label', span: 4, placeholder: 'Deposit Agreement' },
        { key: 'description', label: 'Description', span: 8, placeholder: 'The deposit agreement governing the transaction.' },
        { key: 'case_type', label: 'Case type (blank=all)', span: 3, placeholder: 'deposit_review' },
        { key: 'depositor_type', label: 'Depositor type (blank=all)', span: 3, placeholder: 'individual' },
        { key: 'match_doc_category', label: 'Match doc category', span: 3, select: DOC_CATEGORIES },
        { key: 'match_classification', label: 'Match classification (substring)', span: 3, placeholder: 'agreement' },
        { key: 'match_keywords', label: 'Match keywords (comma-separated)', span: 8, placeholder: 'deposit agreement,deposit contract' },
        { key: 'priority', label: 'Priority', span: 2, type: 'number' },
      ]}
      columns={[
        { header: 'Label', render: (r) => <span className="font-medium text-slate-800">{r.label}</span> },
        { header: 'Applies to', render: (r) => <span className="text-xs text-slate-500">{r.case_type || 'all'} / {r.depositor_type || 'all'}</span> },
        { header: 'Match', render: (r) => <span className="text-xs text-slate-500">{r.match_doc_category || r.match_classification || r.match_keywords || '—'}</span> },
        { header: 'Enabled', render: (r) => <span className={`text-xs ${r.enabled ? 'text-green-600' : 'text-slate-400'}`}>{r.enabled ? '● active' : '○ off'}</span> },
      ]}
    />
  );
}

// ---------------------------------------------------------------------------
// Required Information
// ---------------------------------------------------------------------------

const EMPTY_INFO: RequiredInformationRuleInput = {
  label: '', description: '', case_type: null,
  match_field: null, match_keywords: null,
  enabled: true, priority: 0,
};

function InformationRulesSection() {
  const addToast = useToastStore((s) => s.addToast);
  const [rules, setRules] = useState<RequiredInformationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<RequiredInformationRuleInput>(EMPTY_INFO);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRules(await getRequiredInformationRules()); }
    catch { addToast('Failed to load required-information rules', 'error'); }
    finally { setLoading(false); }
  }, [addToast]);

  useEffect(() => { void load(); }, [load]);

  const reset = () => { setEditingId(null); setForm(EMPTY_INFO); };

  const startEdit = (r: RequiredInformationRule) => {
    setEditingId(r.id);
    setForm({
      label: r.label, description: r.description, case_type: r.case_type,
      match_field: r.match_field, match_keywords: r.match_keywords,
      enabled: r.enabled, priority: r.priority,
    });
  };

  const handleSave = async () => {
    if (!form.label.trim()) { addToast('Label is required', 'error'); return; }
    setSaving(true);
    try {
      const payload = { ...form, label: form.label.trim() };
      if (editingId) {
        await updateRequiredInformationRule(editingId, payload);
        addToast('Required-information rule updated', 'success');
      } else {
        await createRequiredInformationRule(payload);
        addToast('Required-information rule created', 'success');
      }
      reset(); await load();
    } catch (e: any) { addToast(e?.response?.data?.detail || 'Failed to save', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    setSaving(true);
    try { await deleteRequiredInformationRule(id); addToast('Rule deleted', 'success'); await load(); }
    catch (e: any) { addToast(e?.response?.data?.detail || 'Failed to delete', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <Section
      icon={<Info size={15} />}
      title="Required Information"
      subtitle="Information items that must be present — matched against case fact fields + document text."
      loading={loading}
      editingId={editingId}
      form={form as any}
      setForm={setForm as any}
      reset={reset}
      onSave={handleSave}
      saving={saving}
      rules={rules}
      onStartEdit={startEdit as any}
      onDelete={handleDelete}
      fields={[
        { key: 'label', label: 'Label', span: 4, placeholder: 'CUSIP identifier' },
        { key: 'description', label: 'Description', span: 8, placeholder: 'The CUSIP number of the security.' },
        { key: 'case_type', label: 'Case type (blank=all)', span: 3, placeholder: 'deposit_review' },
        { key: 'match_field', label: 'Match case field', span: 3, select: CASE_FACT_FIELDS },
        { key: 'match_keywords', label: 'Match keywords (comma-separated)', span: 6, placeholder: 'cusip' },
        { key: 'priority', label: 'Priority', span: 2, type: 'number' },
      ]}
      columns={[
        { header: 'Label', render: (r) => <span className="font-medium text-slate-800">{r.label}</span> },
        { header: 'Applies to', render: (r) => <span className="text-xs text-slate-500">{r.case_type || 'all'}</span> },
        { header: 'Match', render: (r) => <span className="text-xs text-slate-500">{r.match_field || r.match_keywords || '—'}</span> },
        { header: 'Enabled', render: (r) => <span className={`text-xs ${r.enabled ? 'text-green-600' : 'text-slate-400'}`}>{r.enabled ? '● active' : '○ off'}</span> },
      ]}
    />
  );
}

// ---------------------------------------------------------------------------
// Shared section renderer
// ---------------------------------------------------------------------------

interface FieldDef {
  key: string;
  label: string;
  span: number;
  placeholder?: string;
  type?: 'number';
  select?: string[];
}

// Static map so Tailwind's JIT detects the col-span classes (dynamic
// `sm:col-span-${n}` strings are purged).
const SPAN_CLASS: Record<number, string> = {
  2: 'sm:col-span-2',
  3: 'sm:col-span-3',
  4: 'sm:col-span-4',
  6: 'sm:col-span-6',
  8: 'sm:col-span-8',
};

interface ColumnDef {
  header: string;
  render: (r: any) => React.ReactNode;
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  loading: boolean;
  editingId: string | null;
  form: Record<string, any>;
  setForm: (f: any) => void;
  reset: () => void;
  onSave: () => void;
  saving: boolean;
  rules: any[];
  onStartEdit: (r: any) => void;
  onDelete: (id: string) => void;
  fields: FieldDef[];
  columns: ColumnDef[];
}

function Section({ icon, title, subtitle, loading, editingId, form, setForm, reset, onSave, saving, rules, onStartEdit, onDelete, fields, columns }: SectionProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-primary-600">{icon}</span>
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      </div>
      <p className="text-xs text-slate-500 -mt-1">{subtitle}</p>

      {/* Editor form */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold text-slate-700">{editingId ? 'Edit rule' : 'Add rule'}</h4>
          {editingId && (
            <button onClick={reset} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
              <X size={12} /> Cancel edit
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
          {fields.map((f) => (
            <div key={f.key} className={SPAN_CLASS[f.span] || 'sm:col-span-12'}>
              <label className="block text-xs font-medium text-slate-500 mb-1">{f.label}</label>
              {f.select ? (
                <select
                  className="input text-sm"
                  value={form[f.key] ?? ''}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value || null })}
                >
                  {f.select.map((opt) => (
                    <option key={opt} value={opt}>{opt || '(none)'}</option>
                  ))}
                </select>
              ) : (
                <input
                  className="input text-sm"
                  type={f.type || 'text'}
                  placeholder={f.placeholder}
                  value={form[f.key] ?? ''}
                  onChange={(e) => setForm({ ...form, [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value })}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 pt-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-slate-300 text-primary-600 focus:ring-primary-400"
              checked={form.enabled ?? true}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            Enabled
          </label>
          <button
            onClick={onSave}
            disabled={saving}
            className="btn-primary text-sm px-4 py-2 flex items-center gap-1.5 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {editingId ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-slate-400" size={20} /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  {columns.map((c) => (
                    <th key={c.header} className="px-4 py-3 font-semibold text-slate-600">{c.header}</th>
                  ))}
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rules.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                    {columns.map((c, i) => (
                      <td key={i} className="px-4 py-3">{c.render(r)}</td>
                    ))}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => onStartEdit(r)}
                        disabled={saving}
                        className="text-xs text-primary-600 hover:text-primary-700 mr-3 disabled:opacity-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onDelete(r.id)}
                        disabled={saving}
                        className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {rules.length === 0 && (
                  <tr><td colSpan={columns.length + 1} className="px-4 py-6 text-center text-slate-400 text-sm">No rules yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}